import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Microscope, ExternalLink, Loader2, Users } from "lucide-react";

export default function ClinicalTrialsPanel() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/clinical-trials/search", { params: { q, status: status || undefined, limit: 25 } });
      setRows(data.results || []);
      setTotal(data.total || 0);
      if (!data.results?.length) toast.info("No matching trials");
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  const statusColor = (s) => {
    if (!s) return "text-slate-500 border-slate-200";
    const t = s.toLowerCase();
    if (t.includes("recruiting")) return "text-emerald-300 border-emerald-500/40";
    if (t.includes("completed")) return "text-blue-300 border-blue-500/40";
    if (t.includes("terminated") || t.includes("withdrawn")) return "text-red-300 border-red-500/40";
    return "text-slate-600 border-slate-200";
  };

  return (
    <div>
      <div className="mb-6">
        <div className="overline mb-2 text-slate-500">CLINICALTRIALS.GOV · LIVE</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Clinical evidence search.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Search the U.S. NIH ClinicalTrials.gov database for studies relevant to your device.
          Use the results to support your Clinical Evaluation Report, CER equivalence route,
          and 510(k) performance-testing sections.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <div className="flex flex-1 items-center border border-slate-200 bg-white pl-4">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="ctgov-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="e.g., continuous glucose monitor, pulse oximeter, insulin pump…"
            className="flex-1 border-none bg-transparent px-3 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900 focus:border-[#2563EB] focus:outline-none"
        >
          <option value="">Any status</option>
          <option value="RECRUITING">Recruiting</option>
          <option value="ACTIVE_NOT_RECRUITING">Active, not recruiting</option>
          <option value="COMPLETED">Completed</option>
          <option value="TERMINATED">Terminated</option>
        </select>
        <button
          data-testid="ctgov-search-btn"
          onClick={search}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
        </button>
      </div>

      {rows.length > 0 && (
        <>
          <div className="mb-4 flex items-center justify-between border border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <Microscope className="h-4 w-4 text-[#2563EB]" />
              <div className="overline text-slate-500">{rows.length} SHOWING · {total.toLocaleString()} TOTAL MATCHES</div>
            </div>
          </div>
          <div className="grid gap-3">
            {rows.map((r) => (
              <div key={r.nct_id} data-testid="ctgov-row" className="border border-slate-200 bg-white p-4 hover:border-slate-300">
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <span className={`border px-2 py-0.5 font-mono text-[10px] tracking-wider ${statusColor(r.status)}`}>
                    {r.status || "STATUS N/A"}
                  </span>
                  <span className="font-mono text-xs text-[#93C5FD]">{r.nct_id}</span>
                  {r.phase && <span className="overline text-slate-500">{r.phase}</span>}
                  {r.study_type && <span className="overline text-slate-500">{r.study_type}</span>}
                </div>
                <div className="font-medium text-slate-900">{r.title}</div>
                <div className="mt-2 text-xs text-slate-500">
                  Sponsor: <span className="text-slate-700">{r.sponsor || "—"}</span>
                  {r.enrollment && (<> · <Users className="inline h-3 w-3" /> {r.enrollment} enrolled</>)}
                  {r.start_date && <> · Started {r.start_date}</>}
                </div>
                {r.conditions?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.conditions.slice(0,5).map((c,i) => (
                      <span key={i} className="border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">{c}</span>
                    ))}
                  </div>
                )}
                <a href={r.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-[#93C5FD] hover:text-slate-900">
                  Open on ClinicalTrials.gov <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
